// src/components/ClassRosterPane.jsx
// 授業の受講者名簿【2026-09-17 授業ページから学生管理へ移設】
//   履修者の追加、manaba 名簿の取り込み（コースごとの行き先指定）、
//   名簿の一覧（絞り込み・並べ替え・氏名の修正）と出欠表。
//   学生管理で授業を1つ選んだときに出す。授業ページの「受講者名簿」は学生管理へ統合した。
import React, { useState } from 'react';
import StudentStatusBadges, { STATUS } from './StudentStatusBadges';
import ManabaRosterImport from './ManabaRosterImport';
import ManualEnrollForm from './ManualEnrollForm';
import ClassLinkPanel from './ClassLinkPanel';
import ManualAttendancePanel from './ManualAttendancePanel';
import StudentCustomList, { useStudentListSettings, ViewSwitch } from './StudentCustomList';

// 出欠マークの見せ方
export const MARKS = {
  present:     { mark: '○', color: '#16a34a', label: '出席' },
  late:        { mark: '△', color: '#d97706', label: '遅刻' },
  early_leave: { mark: '◇', color: '#0891b2', label: '早退' },
  // 公欠（学生の申請を認めた回）。出席率の分母に入らない【2026-09-30】
  excused:     { mark: '公', color: '#7c3aed', label: '公欠' },
};
export const ABSENT = { mark: '×', color: '#dc2626', label: '欠席' };

// ===========================================================================
// 受講者名簿
//   履修者の追加と、第何回に出席したかの一覧
//   【2026-09-17】1人ずつの履修の取り消しボタンはやめた（押し間違いで名簿から消えるため）。
//   取り込みを丸ごと戻すのは「名簿取り込み履歴」から。
// ===========================================================================
export default function ClassRosterPane({ classId, auth, token, reload, setErr, setMsg, sessions, students, held, classes = [], cls = null, isStaff = false,
                                          incomingFile = null, onIncomingTaken }) {
  const [query, setQuery]               = useState('');
  // 氏名の手動修正（学生管理から移設）【2026-09-17】
  //   保存すると name_source='manual' になり、名簿の取り込みで上書きされない
  const [editing, setEditing]           = useState(null);   // 学生の users.id
  const [editName, setEditName]         = useState('');
  const [savingName, setSavingName]     = useState(false);
  const saveName = async (stu) => {
    const v = editName.trim();
    if (!v) { setErr('氏名を空にすることはできません'); return; }
    setSavingName(true); setErr(''); setMsg('');
    try {
      const res = await fetch('/api/students/' + stu.id + '/name', {
        method: 'PUT', headers: auth(), body: JSON.stringify({ name: v }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '氏名の更新に失敗しました');
      setMsg('氏名を「' + v + '」に修正しました（名簿の取り込みでも上書きされません）');
      setEditing(null);
      reload();
    } catch (e) { setErr(e.message); }
    finally     { setSavingName(false); }
  };
  // パスワードの再発行（先生・教務）【2026-10-01】
  //   メールが届かない学生のため。仮のパスワードを画面に出すので、学生に伝えてもらう。
  const [resetBusy, setResetBusy] = useState(null);
  const resetPassword = async (stu) => {
    if (!window.confirm(`${stu.student_id} ${stu.name} さんのパスワードを再発行しますか？\n` +
                        '仮のパスワードが表示されます。学生に伝えてください（ログイン中の端末はログアウトされます）。')) return;
    setResetBusy(stu.id); setErr(''); setMsg('');
    try {
      const res = await fetch('/api/students/' + stu.id + '/reset-password', { method: 'POST', headers: auth() });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '再発行に失敗しました');
      setMsg(`${d.loginId} ${d.name || ''} さんの仮のパスワード：「${d.temporaryPassword}」 ─ ` +
             'ログインしたら「アカウント設定」でパスワードを変えてもらってください（この表示は一度きりです）');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setErr(e.message); }
    finally { setResetBusy(null); }
  };
  // 名簿の表示【2026-09-17】
  //   名簿（標準）／カスタム は人ごとに保存する（学生管理の全員表示と共通の設定）。
  //   【2026-09-17】出欠表はやめ、カスタムの「回ごとの出欠」（第何回・日付の列）に移した。
  const [listSettings, updateListSettings, listStored] = useStudentListSettings();
  const rosterView = listSettings.view === 'custom' ? 'custom' : 'roster';
  const setRosterView = (v) => updateListSettings({ view: v === 'custom' ? 'custom' : 'standard' });
  // 「受講者を追加」の開け閉め【2026-09-17】ブラウザが覚える。名簿が空のとき・ファイルを落としたときは開く
  const [addOpen, setAddOpenState] = useState(() => {
    try { return localStorage.getItem('rosterAddOpen') === '1'; } catch (e) { return false; }
  });
  const setAddOpen = (v) => {
    setAddOpenState(v);
    try { localStorage.setItem('rosterAddOpen', v ? '1' : '0'); } catch (e) { /* 覚えられなくても動く */ }
  };
  const addShown = addOpen || students.length === 0 || !!incomingFile;
  const [rosterFilter, setRosterFilter] = useState('all');      // all | enrolled | noAccount | notOnRoster | risk
  const [rosterSort, setRosterSort]     = useState({ key: 'student_id', dir: 1 });

  // 名簿の一覧【2026-09-17】検索・状態の絞り込み・並べ替え
  //   連続欠席は、実施済みの回を新しい順に見て、最初に出席が出るまでの回数
  const ATTENDED = ['present', 'late', 'early_leave'];
  const streakOf = (stu) => {
    let n = 0;
    for (let i = sessions.length - 1; i >= 0; i--) {
      if (ATTENDED.includes(stu.cells[sessions[i].id])) break;
      if (stu.cells[sessions[i].id] === 'excused') continue;   // 公欠は数えない【2026-09-30】
      n++;
    }
    return n;
  };
  const rows = students.map(s => ({ ...s, streak: streakOf(s) }));
  // 別学部の授業をまとめているときの「所属」【2026-09-17】
  const ownName = (cls && cls.name) || '';
  const sectionOf = (s) => (s.is_outside_roster ? '' : (s.origin_class_name || ownName));
  const hasSections = students.some(s => s.origin_class_name);
  const sectionNames = hasSections
    ? [...new Set(students.map(sectionOf).filter(Boolean))].sort((a, b) =>
        (a === ownName ? -1 : b === ownName ? 1 : a.localeCompare(b, 'ja')))
    : [];
  const ROSTER_FILTERS = [
    { key: 'all',         label: 'すべて',                   test: () => true },
    { key: 'enrolled',    label: '履修者',                   test: s => !s.is_outside_roster },
    { key: 'noAccount',   label: STATUS.noAccount.label,     test: s => s.is_placeholder },
    { key: 'notOnRoster', label: STATUS.notOnRoster.label,   test: s => s.is_outside_roster },
    { key: 'risk',        label: '連続欠席2回以上',          test: s => s.streak >= 2 && held > 0 },
    ...sectionNames.map(n => ({ key: 'sec:' + n, label: '所属: ' + n, test: s => sectionOf(s) === n })),
  ];
  const rosterCounts = Object.fromEntries(ROSTER_FILTERS.map(f => [f.key, rows.filter(f.test).length]));
  const fTest = (ROSTER_FILTERS.find(f => f.key === rosterFilter) || ROSTER_FILTERS[0]).test;
  const SORTERS = {
    student_id: (a, b) => String(a.student_id).localeCompare(String(b.student_id), 'ja', { numeric: true }),
    name:       (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ja'),
    rate:       (a, b) => (a.rate == null ? 999 : a.rate) - (b.rate == null ? 999 : b.rate),
    absent:     (a, b) => a.absent - b.absent,
    streak:     (a, b) => a.streak - b.streak,
    section:    (a, b) => sectionOf(a).localeCompare(sectionOf(b), 'ja'),
    enrolled_at:(a, b) => String(a.enrolled_at || a.first_attended || '').localeCompare(String(b.enrolled_at || b.first_attended || '')),
  };
  // カスタム表示の並びは、保存した設定に従う
  const activeSort = rosterView === 'custom' ? listSettings.sort : rosterSort;
  const visible = rows
    .filter(s => {
      if (!fTest(s)) return false;
      if (!query.trim()) return true;
      const q = query.trim().toLowerCase();
      return (s.name || '').toLowerCase().includes(q)
          || (s.roster_name || '').toLowerCase().includes(q)
          || (s.student_id || '').toLowerCase().includes(q);
    })
    .sort((a, b) => (SORTERS[activeSort.key] || SORTERS.student_id)(a, b) * activeSort.dir
                    || SORTERS.student_id(a, b));
  const sortBy = (key) => setRosterSort(cur => (
    cur.key === key ? { key, dir: -cur.dir } : { key, dir: key === 'absent' || key === 'streak' ? -1 : 1 }
  ));
  const SortTh = ({ k, children, style }) => (
    <th onClick={() => sortBy(k)} style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', ...style }}
        title="クリックで並べ替え">
      {children}{rosterSort.key === k ? (rosterSort.dir > 0 ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const ymd = (v) => (v ? String(v).slice(0, 10) : '');
  const countOf = (stu, st) => sessions.filter(s => stu.cells[s.id] === st).length;
  const lastAttendedOf = (stu) => {
    for (let i = sessions.length - 1; i >= 0; i--) {
      if (ATTENDED.includes(stu.cells[sessions[i].id])) return String(sessions[i].date).slice(0, 10);
    }
    return '';
  };

  return (
    <>
      <div className="card" data-add-card>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap',
                                              ...(addShown ? {} : { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }) }}>
          <h2 style={{ margin: 0 }}>受講者を追加</h2>
          {!addShown && (
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>manaba の名簿の取り込み・1人ずつ手で追加</span>
          )}
          <button type="button" className="btn-secondary" aria-expanded={addShown}
                  disabled={students.length === 0 && !addOpen}
                  title={students.length === 0 ? '受講者がいないので開いています' : undefined}
                  style={{ marginLeft: 'auto', padding: '0.2rem 0.7rem', fontSize: '0.85rem' }}
                  onClick={() => setAddOpen(!addShown)}>
            {addShown ? '▲ 閉じる' : '▼ 開く'}
          </button>
        </div>
        {/* 閉じても取り込みの途中の状態を失わないように、隠すだけにする */}
        <div data-add-body style={{ display: addShown ? 'block' : 'none' }}>
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>1人ずつ手で追加</div>
          <ManualEnrollForm classId={classId} auth={auth} setErr={setErr} setMsg={setMsg} onAdded={reload} />
        </div>

        <ManabaRosterImport
          classId={classId}
          cls={cls}
          classes={classes}
          token={token}
          setErr={setErr}
          setMsg={setMsg}
          onImported={reload}
          isStaff={isStaff}
          incomingFile={incomingFile}
          onIncomingTaken={onIncomingTaken}
        />
        </div>
      </div>

      {/* 手動で出席を記録【2026-10-01】 */}
      <ManualAttendancePanel classId={classId} sessions={sessions} students={students}
                             auth={auth} reload={reload} setErr={setErr} setMsg={setMsg} />

      <ClassLinkPanel
        classId={classId}
        className={cls && cls.name}
        classes={classes}
        token={token}
        setErr={setErr}
        setMsg={setMsg}
        onChanged={reload}
      />

      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            受講者名簿（{visible.length}{visible.length !== students.length && ' / ' + students.length}名）
          </h2>
          {/* 検索と表示の切り替えは、全員表示と同じく右側に置く */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <input value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="氏名・学籍番号で検索" aria-label="受講者を検索"
                   style={{ padding: '0.3rem 0.5rem', minWidth: 170 }} />
            <ViewSwitch value={rosterView} onChange={setRosterView}
                        options={[['roster', '名簿'], ['custom', 'カスタム']]} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          {ROSTER_FILTERS.filter(f => f.key === 'all' || rosterCounts[f.key] > 0).map(f => (
            <button key={f.key} type="button" onClick={() => setRosterFilter(f.key)}
                    style={{ fontSize: '0.8rem', padding: '0.12rem 0.6rem', borderRadius: 999, cursor: 'pointer',
                             border: '1px solid #cbd5e1',
                             background: rosterFilter === f.key ? '#334155' : '#fff',
                             color: rosterFilter === f.key ? '#fff' : '#334155' }}>
              {f.label} {rosterCounts[f.key]}
            </button>
          ))}
        </div>

        {students.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '1.5rem' }}>
            受講者がいません。上の「manabaの名簿から取り込む」か、学籍番号で追加してください。
          </p>
        ) : visible.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '1.5rem' }}>条件に合う学生はいません</p>
        ) : rosterView === 'custom' ? (
          <StudentCustomList
            settings={listSettings}
            update={updateListSettings}
            stored={listStored}
            sortKeys={['student_id', 'name', 'rate', 'absent', 'streak', ...(hasSections ? ['section'] : [])]}
            sinceLabel="履修登録日"
            sessions={sessions}
            legend={[...Object.values(MARKS), ABSENT]}
            items={visible.map(stu => ({
              key: stu.id,
              raw: stu,
              studentId: stu.student_id,
              name: stu.name,
              rate: stu.rate != null ? Number(stu.rate) : null,
              placeholder: stu.is_placeholder,
              outside: stu.is_outside_roster,
              // カスタム表示で ON/OFF できる項目の値
              v: {
                attended: stu.attended, held: stu.held ?? held, absent: stu.absent, streak: stu.streak,
                late: countOf(stu, 'late'), early: countOf(stu, 'early_leave'),
                last: lastAttendedOf(stu), section: hasSections ? sectionOf(stu) : '',
                since: stu.is_outside_roster ? ymd(stu.first_attended) : ymd(stu.enrolled_at),
                rosterName: stu.roster_name,
                marks: sessions.map(s => {
                  const m = MARKS[stu.cells[s.id]] || ABSENT;
                  return { mark: m.mark, color: m.color, label: `第${s.no}回 ${String(s.date).slice(5, 10)} ${m.label}` };
                }),
              },
            }))}
            renderEdit={(it) => (editing === it.raw.id ? (
              <span style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}>
                <input value={editName} autoFocus aria-label="氏名"
                       onChange={e => setEditName(e.target.value)}
                       onKeyDown={e => {
                         if (e.key === 'Enter') saveName(it.raw);
                         if (e.key === 'Escape') setEditing(null);
                       }}
                       style={{ padding: '0.1rem 0.3rem', width: 130 }} />
                <button onClick={() => saveName(it.raw)} disabled={savingName}>保存</button>
                <button onClick={() => setEditing(null)} disabled={savingName}>やめる</button>
              </span>
            ) : (
              <button onClick={() => { setEditing(it.raw.id); setEditName(it.raw.name || ''); }}
                      style={{ fontSize: '0.76rem', padding: '0 0.45rem' }}>修正</button>
            ))}
          />
        ) : rosterView === 'roster' ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: 'right' }}>#</th>
                  <SortTh k="student_id">学籍番号</SortTh>
                  <SortTh k="name">氏名</SortTh>
                  <th>状態</th>
                  {hasSections && <SortTh k="section">所属</SortTh>}
                  <SortTh k="rate" style={{ textAlign: 'right' }}>出席率</SortTh>
                  <SortTh k="absent" style={{ textAlign: 'right' }}>欠席</SortTh>
                  <SortTh k="streak" style={{ textAlign: 'right' }}>連続欠席</SortTh>
                  <SortTh k="enrolled_at">履修登録日</SortTh>
                </tr>
              </thead>
              <tbody>
                {visible.map((stu, i) => (
                  <tr key={stu.id}>
                    <td style={{ textAlign: 'right', color: '#9ca3af' }}>{i + 1}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{stu.student_id}</td>
                    <td>
                      {editing === stu.id ? (
                        <span style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                          <input value={editName} autoFocus
                                 onChange={e => setEditName(e.target.value)}
                                 onKeyDown={e => {
                                   if (e.key === 'Enter') saveName(stu);
                                   if (e.key === 'Escape') setEditing(null);
                                 }}
                                 style={{ padding: '0.2rem 0.35rem', minWidth: 140 }} />
                          <button onClick={() => saveName(stu)} disabled={savingName}>保存</button>
                          <button onClick={() => setEditing(null)} disabled={savingName}>やめる</button>
                        </span>
                      ) : (
                        <>
                          <strong>{stu.name}</strong>
                          {stu.name_source === 'manual' && (
                            <span title="手動で修正済み。名簿の取り込みでも上書きされません"
                                  style={{ marginLeft: '0.3rem', fontSize: '0.66rem', color: '#0a7',
                                           border: '1px solid #0a7', borderRadius: 4, padding: '0 3px' }}>
                              修正済
                            </span>
                          )}
                          <button type="button" title="氏名を修正"
                                  onClick={() => { setEditing(stu.id); setEditName(stu.name || ''); }}
                                  style={{ marginLeft: '0.3rem', fontSize: '0.68rem', padding: '0 0.3rem',
                                           background: 'none', border: '1px solid #d1d5db', borderRadius: 4,
                                           color: '#555', cursor: 'pointer' }}>
                            修正
                          </button>
                          {stu.roster_name && stu.roster_name !== stu.name && (
                            <div style={{ fontSize: '0.72rem', color: '#c67' }}>名簿: {stu.roster_name}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {!stu.is_outside_roster && !stu.is_placeholder && (
                        <span style={{ fontSize: '0.72rem', color: '#166534' }}>履修中</span>
                      )}
                      <StudentStatusBadges placeholder={stu.is_placeholder}
                                           outside={stu.is_outside_roster} size="xs" />
                      {!stu.is_outside_roster && !stu.is_placeholder && (
                        <button type="button" title="パスワードを忘れた学生に、仮のパスワードを発行します"
                                disabled={resetBusy === stu.id} onClick={() => resetPassword(stu)}
                                style={{ marginLeft: '0.3rem', fontSize: '0.68rem', padding: '0 0.3rem',
                                         background: 'none', border: '1px solid #d1d5db', borderRadius: 4,
                                         color: '#555', cursor: 'pointer' }}>
                          {resetBusy === stu.id ? '…' : 'パスワード再発行'}
                        </button>
                      )}
                    </td>
                    {hasSections && (
                      <td style={{ fontSize: '0.8rem', color: stu.origin_class_name ? '#1d4ed8' : '#555' }}>
                        {sectionOf(stu) || '—'}
                      </td>
                    )}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <strong>{stu.rate != null ? stu.rate + '%' : '-'}</strong>
                      <div style={{ fontSize: '0.7rem', color: '#888' }}>{stu.attended}/{stu.held ?? held}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{held > 0 ? stu.absent : '-'}</td>
                    <td style={{ textAlign: 'right', color: stu.streak >= 2 ? '#dc2626' : undefined,
                                 fontWeight: stu.streak >= 2 ? 700 : 400 }}>
                      {held > 0 ? stu.streak : '-'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: '#555' }}>
                      {stu.is_outside_roster
                        ? <span title="名簿に無いので履修登録日はありません。最初に打刻した日">初回打刻 {ymd(stu.first_attended)}</span>
                        : ymd(stu.enrolled_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </>
  );
}
